import type {
  ApplicationFieldConfig,
  FieldCondition,
} from '../types/audition.types';

/**
 * Shared evaluation engine for application-form conditions (display +
 * validity). Mirrors the Meteor `checkLogic` engine (parity map §2/§8): the
 * existing equality/emptiness operators plus numeric and date comparisons.
 *
 * Used by the renderer (field/step visibility) and the builder (option
 * resolution). Pure + side-effect-free so it can be unit-tested in isolation.
 */

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (
    typeof value === 'string' &&
    value.trim() !== '' &&
    Number.isFinite(Number(value))
  ) {
    return Number(value);
  }
  return null;
}

function asDate(value: unknown): number | null {
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

/** Numeric-then-date comparable, or null when neither applies. */
function comparable(value: unknown): number | null {
  return asNumber(value) ?? asDate(value);
}

function isEmptyValue(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

/** Evaluate a single condition against the current form values. */
export function evaluateCondition(
  condition: FieldCondition,
  values: Record<string, unknown>,
): boolean {
  const fieldValue = values[condition.fieldKey];
  const target = condition.value;

  switch (condition.operator) {
    case 'equals':
      return fieldValue === target;
    case 'notEquals':
      return fieldValue !== target;
    case 'contains':
      if (Array.isArray(fieldValue) && typeof target === 'string') {
        return fieldValue.includes(target);
      }
      if (typeof fieldValue === 'string' && typeof target === 'string') {
        return fieldValue.includes(target);
      }
      return false;
    case 'notContains':
      if (Array.isArray(fieldValue) && typeof target === 'string') {
        return !fieldValue.includes(target);
      }
      if (typeof fieldValue === 'string' && typeof target === 'string') {
        return !fieldValue.includes(target);
      }
      return true;
    case 'isEmpty':
      return isEmptyValue(fieldValue);
    case 'isNotEmpty':
      return !isEmptyValue(fieldValue);
    case 'greaterThan':
    case 'lessThan':
    case 'greaterOrEqual':
    case 'lessOrEqual': {
      const a = comparable(fieldValue);
      const b = comparable(target);
      if (a === null || b === null) return false;
      if (condition.operator === 'greaterThan') return a > b;
      if (condition.operator === 'lessThan') return a < b;
      if (condition.operator === 'greaterOrEqual') return a >= b;
      return a <= b;
    }
    case 'onOrBefore':
    case 'onOrAfter': {
      const a = asDate(fieldValue);
      const b = asDate(target);
      if (a === null || b === null) return false;
      return condition.operator === 'onOrBefore' ? a <= b : a >= b;
    }
    default:
      return true;
  }
}

/** True when the conditions are satisfied under the given and/or logic. */
export function areConditionsMet(
  conditions: FieldCondition[] | undefined,
  logic: 'and' | 'or' | undefined,
  values: Record<string, unknown>,
): boolean {
  if (!conditions || conditions.length === 0) return true;
  return logic === 'or'
    ? conditions.some((c) => evaluateCondition(c, values))
    : conditions.every((c) => evaluateCondition(c, values));
}

/** Whether a field is visible given its display conditions. */
export function isFieldVisible(
  field: Pick<ApplicationFieldConfig, 'conditions' | 'conditionLogic'>,
  values: Record<string, unknown>,
): boolean {
  return areConditionsMet(field.conditions, field.conditionLogic, values);
}
